/**
 * Outreach — Theresa's "Sourcing & Outreach" layout, its own page under Talent Sourcing (next to
 * its Email Library of InMail templates): Campaigns (every Open and Backlog role) and Needs you,
 * then one campaign in four guided steps (CampaignView): Brief, Find people, Write & send, Log.
 *
 * InMails go out through the FINN Outreach Sender extension (`chrome-extension/outreach-sender`):
 * one "Start" click, the extension types and sends in the recruiter's own LinkedIn Recruiter tab, and
 * this page records it. The proxy never touches linkedin.com. Everything is scoped to the signed-in
 * recruiter. Navigation lives in the URL (`?posting=`, `?view=`, `?top=`) because the space reloads
 * itself after 15 idle minutes.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../../../shared';
import * as api from '../../services/taOutreachApi';
import type { Campaign, OutreachConfig } from '../../types/taOutreach';
import { CampaignView, type CampaignSub } from './CampaignView';
import { CampaignsList } from './CampaignsList';
import { hasOwnNav, loadNav, saveNav } from './outreachNav';

const SUBS: CampaignSub[] = ['brief', 'sourcing', 'candidates', 'queue', 'contacted', 'replies'];
const FALLBACK_CONFIG: OutreachConfig = { testMode: true, model: '', maxRows: 200, maxBodyChars: 1200, maxSubjectChars: 80 };

export function OutreachTab({ myEmail, onCountChange }: { myEmail: string; onCountChange?: (n: number) => void }) {
  const [params, setParams] = useSearchParams();
  const postingId = params.get('posting') || '';
  const subParam = params.get('view') as CampaignSub | null;
  const top = params.get('top') === 'needsyou' ? 'needsyou' : 'campaigns';
  const focusId = params.get('candidate');
  const scope = params.get('scope') === 'all' ? 'all' : 'mine';
  const status = params.get('status') === 'Open' || params.get('status') === 'Backlog' ? (params.get('status') as 'Open' | 'Backlog') : 'all';
  const q = params.get('q') || '';
  const dept = params.get('dept') || '';
  const owner = params.get('owner') || '';

  const [config, setConfig] = useState<OutreachConfig | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const setParam = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  // Coming back via the sidebar (a bare /outreach) reopens where the recruiter left off; a link
  // that names its own target wins. Every change is remembered — going back to the list included.
  // A ref, not state: whether the one restore has happened changes nothing on screen.
  const restored = useRef(false);
  useEffect(() => {
    if (!myEmail) return;
    if (!restored.current) {
      restored.current = true;
      const saved = hasOwnNav(params) ? null : loadNav(myEmail);
      if (saved) {
        setParams(saved, { replace: true });
        return; // the next render, with the restored query, is the one remembered
      }
    }
    saveNav(myEmail, params);
  }, [myEmail, params, setParams]);

  useEffect(() => {
    api.fetchOutreachConfig().then(setConfig).catch(() => setConfig(FALLBACK_CONFIG));
  }, []);

  // Re-read on returning to the list, so the counts reflect what was just done in a campaign.
  useEffect(() => {
    if (postingId) return;
    api
      .fetchCampaigns()
      .then((r) => {
        setCampaigns(r.campaigns);
        setError(null);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Could not read the hiring plan.'))
      .finally(() => setLoading(false));
  }, [postingId]);

  // A reminder links here with ?candidate= only: find its role once.
  useEffect(() => {
    if (!focusId || postingId) return;
    api
      .fetchOutreach()
      .then((r) => {
        const hit = r.items.find((c) => c.id === focusId);
        // A follow-up reminder is about a SENT candidate, and those live under Contacted.
        if (hit) setParam({ posting: hit.postingId, view: hit.status === 'sent' ? 'contacted' : 'queue' });
      })
      .catch(() => {});
  }, [focusId, postingId]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsYou = campaigns.reduce((n, c) => n + c.counts.drafts, 0);
  const cfg = config || FALLBACK_CONFIG;

  return (
    <div className="space-y-4 py-4">
      {cfg.testMode && (
        <div role="status" className="rounded-md border border-solid border-[#F5C26B] bg-[#FFF8E6] px-4 py-3 text-[13px] text-[#7A4B00]">
          <strong>TEST MODE.</strong> Nothing is written to Lever and no reminders are created; the lists are kept apart from the real ones. A
          booking does not hand anyone over to Lever: the Log shows what would have happened.
          “Start” in the Send box still sends <strong>real InMails</strong> in LinkedIn — use it only for people who may get one.
        </div>
      )}

      {postingId ? (
        <CampaignView
          key={postingId}
          postingId={postingId}
          campaign={campaigns.find((c) => c.postingId === postingId) || null}
          config={cfg}
          sub={subParam && SUBS.includes(subParam) ? subParam : 'brief'}
          onSub={(s) => setParam({ view: s === 'brief' ? null : s })}
          focusId={focusId}
          onBack={() => setParam({ posting: null, view: null, candidate: null })}
          onCountChange={onCountChange}
        />
      ) : (
        <>
          <div className="mb-[22px] flex flex-wrap gap-1 border-b border-solid border-[#E9EAEC] pb-2.5">
            {(['campaigns', 'needsyou'] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-current={top === t ? 'page' : undefined}
                onClick={() => setParam({ top: t === 'campaigns' ? null : t })}
                className={cn(
                  'inline-flex items-center gap-[7px] rounded-md border border-solid px-4 py-2.5 text-[15px] font-semibold',
                  top === t ? 'border-[#E9EAEC] bg-[#F8F8F8] text-[#1A1A1A]' : 'border-transparent text-[#707070] hover:bg-[#F8F8F8] hover:text-[#1A1A1A]'
                )}
              >
                {t === 'campaigns' ? 'Campaigns' : 'Needs you'}
                {t === 'needsyou' && needsYou > 0 && <span className="text-xs font-normal text-[#979797]">{needsYou}</span>}
              </button>
            ))}
          </div>
          {top === 'campaigns' ? (
            <CampaignsList
              campaigns={campaigns}
              loading={loading}
              error={error}
              myEmail={myEmail}
              scope={scope}
              status={status}
              q={q}
              dept={dept}
              owner={owner}
              onFilter={(f) => setParam(f)}
              onOpen={(c) => c.postingId && setParam({ posting: c.postingId, top: null })}
            />
          ) : (
            <div className="space-y-3">
              <p className="text-[13px] text-[#707070]">Across every campaign: drafts waiting for your review.</p>
              {campaigns
                .filter((c) => c.counts.drafts > 0)
                .map((c) => (
                  <button
                    key={c.postingId || c.title}
                    type="button"
                    onClick={() => setParam({ posting: c.postingId, view: 'queue', top: null })}
                    className="block w-full rounded-md border border-solid border-[#E9EAEC] bg-white px-[22px] py-[14px] text-left text-[13px] hover:border-[#979797]"
                  >
                    <b>{c.title}</b> — {c.counts.drafts} draft{c.counts.drafts === 1 ? '' : 's'} to review
                  </button>
                ))}
              {!needsYou && <p className="text-[13px] text-[#707070]">No drafts waiting.</p>}
            </div>
          )}
        </>
      )}
    </div>
  );
}
